import { createContext, PropsWithChildren, useContext, useMemo } from 'react'

import { defaultTheme } from '@helpscout/hsds-tokens'

import ScopeProvider from 'hsds/components/scope-provider'

import GlobalStyle, { injectGlobalTokens, ThemeName } from './GlobalStyle'

type GlobalContextType = {
  getCurrentScope: () => string
  theme: {
    colors: {
      default: typeof defaultTheme.color
    }
    name: ThemeName
  }
}

export const GlobalContext = createContext<GlobalContextType>(
  undefined as unknown as GlobalContextType
)

export const useTheme = () => {
  return useContext(GlobalContext)?.theme || {}
}

export const HSDS_SCOPE = 'hsds-react'

type Props = {
  scope?: string
  themeName?: ThemeName
  className?: string
}

const Provider = (props: PropsWithChildren<Props>) => {
  const {
    scope = HSDS_SCOPE,
    themeName = 'default',
    className,
    children,
  } = props

  // Safety net: re-inject tokens if the style tag was removed (e.g., iframe DOM reset)
  injectGlobalTokens()

  const theme = useMemo(() => {
    return {
      name: themeName,
      colors: {
        default: defaultTheme.color,
      },
    }
  }, [themeName])

  const contextValue = useMemo(() => {
    return {
      getCurrentScope: () => scope,
      theme,
    }
  }, [scope, theme])

  return (
    <>
      <GlobalStyle scope={`.${scope}`} themeName={themeName} />
      <GlobalContext.Provider value={contextValue}>
        <ScopeProvider scope={scope} className={className}>
          {children}
        </ScopeProvider>
      </GlobalContext.Provider>
    </>
  )
}

Provider.displayName = 'HSDSProvider'

export default Provider
